import React from 'react';
import { FileText, Check, Crown, Globe, Briefcase, Palette, X } from 'lucide-react';

const License = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-turquoise-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <div className="flex items-center mb-8">
            <div className="bg-cyan-100 p-4 rounded-xl mr-4">
              <FileText className="w-8 h-8 text-cyan-600" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-gray-900">Content License</h1>
              <p className="text-gray-600 mt-2">Last updated: January 11, 2025</p>
            </div>
          </div>

          <div className="mb-8 p-6 bg-gradient-to-br from-cyan-50 to-turquoise-50 rounded-xl border-l-4 border-cyan-600">
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Your Content, Your Rights</h3>
            <p className="text-gray-700 leading-relaxed">
              Content generated through Fluxara belongs to you. You have broad rights to use, modify, and distribute your AI-generated T-shirt designs for both personal and commercial purposes with minimal restrictions.
            </p>
          </div>

          <div className="prose max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Crown className="w-6 h-6 mr-2 text-cyan-600" />
                Ownership & Rights
              </h2>
              
              <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500 mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Full Ownership</h3>
                <p className="text-gray-700 leading-relaxed">
                  You own all AI-generated content created through our platform, including images and any derivatives you create from them.
                </p>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">What You Own</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Generated T-shirt designs and images</li>
                <li>Edited and modified versions</li>
                <li>Derivative works you create</li>
                <li>Compilations and collections</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 mb-3 mt-6">Your Rights Include</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Use for any legal purpose</li>
                <li>Commercial and non-commercial use</li>
                <li>Modify, edit, and transform</li>
                <li>Distribute and share freely</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <Globe className="w-6 h-6 mr-2 text-cyan-600" />
                Usage Rights & Permissions
              </h2>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-blue-50 p-5 rounded-xl border-l-4 border-blue-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Personal Use</h3>
                  <p className="text-gray-700 text-sm">
                    Use your generated content for personal projects, social media, portfolios, and creative endeavors without restrictions.
                  </p>
                </div>

                <div className="bg-cyan-50 p-5 rounded-xl border-l-4 border-cyan-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Commercial Use</h3>
                  <p className="text-gray-700 text-sm">
                    Sell, license, or use your content in commercial products, marketing materials, websites, and business applications.
                  </p>
                </div>
              </div>

              <div className="bg-turquoise-50 p-5 rounded-xl border-l-4 border-turquoise-500">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Distribution</h3>
                <p className="text-gray-700 text-sm">
                  Share, publish, and distribute your content across any platform or medium, including print, digital, and broadcast media.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Specific Permitted Uses</h2>
              
              <div className="grid md:grid-cols-2 gap-6">
                <div className="bg-white border-2 border-cyan-200 p-6 rounded-xl">
                  <div className="flex items-center mb-3">
                    <Palette className="w-6 h-6 text-cyan-600 mr-2" />
                    <h3 className="text-lg font-bold text-gray-900">Creative & Artistic</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Art exhibitions and galleries</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Creative portfolios and showcases</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Educational and research purposes</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Personal and artistic expression</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Social media content creation</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-white border-2 border-turquoise-200 p-6 rounded-xl">
                  <div className="flex items-center mb-3">
                    <Briefcase className="w-6 h-6 text-turquoise-600 mr-2" />
                    <h3 className="text-lg font-bold text-gray-900">Commercial & Business</h3>
                  </div>
                  <ul className="space-y-2 text-gray-700 text-sm">
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Fashion and product marketing</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>E-commerce and retail applications</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Website and app content</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Advertising and promotional materials</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0 mt-0.5" />
                      <span>Print and digital publications</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border-l-4 border-green-500">
                <h3 className="text-xl font-bold text-gray-900 mb-2">No Attribution Required</h3>
                <p className="text-gray-700 leading-relaxed">
                  You are not required to credit Fluxara when using your generated content, though attribution is always appreciated. You may use the content as if it were originally created by you.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                <X className="w-6 h-6 mr-2 text-red-600" />
                Usage Restrictions
              </h2>
              
              <div className="bg-red-50 p-6 rounded-xl border-l-4 border-red-500 mb-4">
                <h3 className="text-lg font-bold text-gray-900 mb-3">Prohibited Uses</h3>
                <p className="text-gray-700 mb-3">
                  While you have broad rights to your generated content, certain uses are prohibited:
                </p>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li className="flex items-start">
                    <X className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Illegal activities or content that violates laws</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Defamatory, harassing, or harmful content</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Content that infringes on others' rights or privacy</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Misleading or fraudulent representations</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Content that promotes violence or hatred</span>
                  </li>
                  <li className="flex items-start">
                    <X className="w-4 h-4 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
                    <span>Adult or inappropriate content involving minors</span>
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 p-6 rounded-xl border-l-4 border-yellow-500">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Important Considerations</h3>
                <ul className="space-y-2 text-gray-700 text-sm">
                  <li>• Respect third-party rights when uploading source prompts</li>
                  <li>• Be mindful of privacy when generating content</li>
                  <li>• Consider local laws and regulations in your jurisdiction</li>
                  <li>• Use responsibly and ethically in all applications</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Technical & Legal Considerations</h2>
              
              <div className="space-y-4">
                <div className="bg-gray-50 p-5 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Content Quality & Authenticity</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• AI-generated content may have identifiable characteristics</li>
                    <li>• Quality may vary based on input parameters</li>
                    <li>• Content is created using advanced AI models</li>
                    <li>• No guarantee of uniqueness across all users</li>
                  </ul>
                </div>

                <div className="bg-gray-50 p-5 rounded-xl">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Legal Framework</h3>
                  <ul className="text-sm text-gray-700 space-y-1">
                    <li>• Rights granted under applicable copyright law</li>
                    <li>• Subject to local and international regulations</li>
                    <li>• May be affected by future AI content legislation</li>
                    <li>• User remains responsible for lawful use</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">License Summary</h2>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-gradient-to-br from-cyan-500 to-turquoise-500 p-6 rounded-xl text-white text-center">
                  <Crown className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-lg font-bold mb-1">Full Ownership</h3>
                  <p className="text-sm opacity-90">Content belongs to you</p>
                </div>

                <div className="bg-gradient-to-br from-blue-500 to-cyan-500 p-6 rounded-xl text-white text-center">
                  <Briefcase className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-lg font-bold mb-1">Commercial Use</h3>
                  <p className="text-sm opacity-90">Sell and monetize freely</p>
                </div>

                <div className="bg-gradient-to-br from-green-500 to-emerald-500 p-6 rounded-xl text-white text-center">
                  <Check className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-lg font-bold mb-1">No Attribution</h3>
                  <p className="text-sm opacity-90">Credit not required</p>
                </div>

                <div className="bg-gradient-to-br from-pink-500 to-rose-500 p-6 rounded-xl text-white text-center">
                  <Globe className="w-12 h-12 mx-auto mb-3" />
                  <h3 className="text-lg font-bold mb-1">Global Rights</h3>
                  <p className="text-sm opacity-90">Use anywhere, anytime</p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Questions About Licensing?</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Need clarification on usage rights or have specific licensing questions?
              </p>
              <div className="bg-gradient-to-br from-cyan-50 to-turquoise-50 p-6 rounded-xl border border-cyan-200">
                <p className="text-gray-700 mb-2"><strong>Email:</strong> support@fluxaraai.com</p>
                <p className="text-gray-700"><strong>Legal Team:</strong> support@fluxaraai.com</p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-gray-100 rounded-lg text-center">
              <p className="text-sm text-gray-600">
                This license is effective immediately upon content generation and continues indefinitely unless terminated by mutual agreement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default License;